import { Logger, $, store } from 'lib'
import locale from 'Common/Locale/Locale'
import util from 'Common/Util/Util'
import settingsService from 'Common/Service/Settings/Settings'

const fallbackStreamLocale = 'en-US'
const userConfigKeySuffix = 'watch-config'

const providersWithoutIESupport = ['nimotv', 'openrec']

const StreamProviders = Object.freeze({
  AfreecaTv: 'afreecatv',
  Kick: 'kick',
  Mildom: 'mildom',
  Nimotv: 'nimotv',
  Openrec: 'openrec',
  Soop: 'soop',
  Trovo: 'trovo',
  Twitch: 'twitch',
  Youtube: 'youtube'
})

/**
 * @typedef {typeof StreamProviders[keyof typeof StreamProviders]} StreamProvider {@link StreamProviders}
 */

/**
 * The providers that should only be selected via the randomization logic if they are the only option available.
 */
const RANDOMIZATION_HESITANT_STREAM_PROVIDERS = new Set([
  StreamProviders.AfreecaTv
])

// Riotbar region names from here:
// https://gh.riotgames.com/web/riotbar/blob/13f83f15157669d55d5493eda1635cfaabd3a2d5/src/i18n.js#L63
const defaultLeagueByRegion = {
  na: 'lcs',
  euw: 'lec',
  eune: 'lec',
  lan: 'lla',
  las: 'lla',
  br: 'cblol-brazil',
  tr: 'turkiye-sampiyonluk-ligi',
  ru: 'lec',
  oce: 'oce-opl',
  kr: 'lck',
  jp: 'ljl-japan'
}

/**
 * @type {Record<string, Set<StreamProvider>>}
 */
const DEFAULT_STREAM_PROVIDERS_BY_LANGUAGE = {
  en: new Set([
    StreamProviders.Kick,
    StreamProviders.Twitch,
    StreamProviders.Youtube,
    StreamProviders.Soop
  ]),
  es: new Set([
    StreamProviders.Kick,
    StreamProviders.Twitch,
    StreamProviders.Youtube
  ]),
  pt: new Set([
    StreamProviders.Kick,
    StreamProviders.Twitch,
    StreamProviders.Youtube
  ]),
  tr: new Set([
    StreamProviders.Kick,
    StreamProviders.Nimotv,
    StreamProviders.Twitch,
    StreamProviders.Youtube
  ])
}

/**
 * @type {Record<string, Set<StreamProvider>>}
 */
const DEFAULT_STREAM_PROVIDERS_BY_LOCALE = {
  'ja-JP': new Set([StreamProviders.Kick, StreamProviders.Twitch]),
  'ko-KR': new Set([StreamProviders.AfreecaTv])
}

const FALLBACK_STREAM_LOCALE = 'en-US'

/**
 * Attempts to return the first eligible stream while honoring the hesitant stream providers.
 * @param {Stream[]} eligibleShuffledStreams
 * @returns {Stream | undefined}
 */
function getFirstEligibleStream (eligibleShuffledStreams) {
  if (!eligibleShuffledStreams.length) {
    return undefined
  }

  const hasOneProvider = eligibleShuffledStreams.length === 1
  const firstStream = eligibleShuffledStreams[0]

  if (!RANDOMIZATION_HESITANT_STREAM_PROVIDERS.has(firstStream.provider)) {
    return firstStream
  }

  if (hasOneProvider) {
    return firstStream
  }

  return eligibleShuffledStreams[1]
}

/**
 * @typedef {Object} MediaLocale
 * @property {string} englishName
 * @property {string} locale
 * @property {string} translatedName
 */

/**
 * @typedef {Object} Stream
 * @property {string[] | null} countries
 * @property {number | null} endMillis
 * @property {string} firstFrameTime
 * @property {string} id
 * @property {string} locale
 * @property {MediaLocale} mediaLocale
 * @property {number} offset
 * @property {string} parameter
 * @property {StreamProvider} provider
 * @property {number | null} startMillis
 */

class WatchUtil {
  constructor () {
    this.log = new Logger(this.constructor.name)
  }

  getDefaultLeagueByRegion (region) {
    return defaultLeagueByRegion[region]
  }

  /**
   * Randomizes the order of the array's elements.
   * @template TArrayElement
   * @param {Array<TArrayElement>} array
   */
  shuffle (array) {
    const result = [...array]

    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      const temp = result[i]

      result[i] = result[j]
      result[j] = temp
    }

    return result
  }

  /**
   * Determines the default stream using the session and available streams.
   *
   * @param {string | null} streamParameter The currently selected stream's parameter.
   * @param {Stream[]} streams
   *
   * @returns {Stream}
   */
  getInitialPrioritizedStream (streams, streamParameter) {
    // We'll check if your desired stream is available
    const desiredStream = streams.find(
      (stream) => stream.parameter === streamParameter
    )

    if (desiredStream) {
      return desiredStream
    }

    // We'll check if your preferred stream provider from your settings is available for your locale
    const currentLocale = locale.get()
    const settings = store.get('settings')
    const preferredStreamProvider = util.getDeepValue(
      settings,
      'watchSettings.defaultVideoProvider'
    )

    const preferredStreamProviderInLocale = streams.find(
      (stream) =>
        stream.locale === currentLocale
        && stream.provider === preferredStreamProvider
    )

    if (preferredStreamProviderInLocale) {
      return preferredStreamProviderInLocale
    }

    // We'll check if your preferred stream provider from your settings is available for your language
    const {
      country: currentCountryCode,
      language: currentLanguage
    } = locale.getParts()

    const preferredStreamProviderInLanguage = streams.find(
      (stream) =>
        locale.getParts(stream.locale).language === currentLanguage
        && stream.provider === preferredStreamProvider
    )

    if (preferredStreamProviderInLanguage) {
      return preferredStreamProviderInLanguage
    }

    // Since there are no preffered streams we'll determine one randomly through different criteria
    const shuffledStreams = this.shuffle(streams)
    const defaultStreamProviders
      = DEFAULT_STREAM_PROVIDERS_BY_LOCALE[currentLocale]
      || DEFAULT_STREAM_PROVIDERS_BY_LANGUAGE[currentLanguage]
      || new Set()

    // We'll find the streams that match the current locale and are either globally available or are available for the current country
    const eligibleStreams = shuffledStreams.filter((stream) => {
      if (!defaultStreamProviders.has(stream.provider)) {
        return false
      }

      if (stream.locale !== currentLocale) {
        return false
      }

      if (!stream.countries?.length) {
        return true
      }

      return stream.countries.includes(currentCountryCode)
    })

    // if we found at least one eligible stream then we'll simply return the first, we can do this since we filtered off of the already shuffled streams
    const eligibleStream = getFirstEligibleStream(eligibleStreams)

    if (eligibleStream) {
      return eligibleStream
    }

    // We'll get a random non-globally available stream
    const nonGlobalEligibleStreams = shuffledStreams.filter(
      (stream) =>
        stream.countries?.length
        && stream.countries.includes(currentCountryCode)
        && stream.locale === locale
    )

    const nonGlobalEligibleStream = getFirstEligibleStream(
      nonGlobalEligibleStreams
    )

    if (nonGlobalEligibleStream) {
      return nonGlobalEligibleStream
    }

    // We'll get a random stream based on your locale
    const localeEligibleStreams = shuffledStreams.filter(
      (stream) => stream.locale === locale
    )

    const localeEligibleStream = getFirstEligibleStream(localeEligibleStreams)

    if (localeEligibleStream) {
      return localeEligibleStream
    }

    // We'll get a random stream based on your language
    const languageEligibleStreams = shuffledStreams.filter(
      (stream) => locale.getParts(stream.locale).language === currentLanguage
    )

    const languageEligibleStream = getFirstEligibleStream(
      languageEligibleStreams
    )

    if (languageEligibleStream) {
      return languageEligibleStream
    }

    // We'll get the fallback stream
    const fallbackLocaleEligibleStreams = shuffledStreams.filter(
      (stream) => stream.locale === FALLBACK_STREAM_LOCALE
    )

    const fallbackLocaleEligibleStream = getFirstEligibleStream(
      fallbackLocaleEligibleStreams
    )

    if (fallbackLocaleEligibleStream) {
      return fallbackLocaleEligibleStream
    }

    // We'll return the first one in the list
    return shuffledStreams[0]
  }

  getStreamByParameter (streams, parameter) {
    const streamByParam = streams.find(
      (stream) => stream.parameter === parameter
    )

    return streamByParam
  }

  // Return stream from the streams parameter in order of priority:
  // 1. Stream having the passed in locale
  // 2. Stream matching the fallback locale
  // 3. First stream in the streams list
  getStreamByLocale (streams, locale) {
    const streamByLocale = streams.find((stream) => stream.locale === locale)

    if (streamByLocale) return streamByLocale

    this.log.warn(
      `No stream found for ${locale}, `
        + `falling back to ${fallbackStreamLocale}`
    )

    const streamByFallbackLocale = this.getFallbackStream(streams)

    if (streamByFallbackLocale) return streamByFallbackLocale

    this.log.warn(
      `No stream found for ${fallbackStreamLocale}, `
        + `falling back to first stream in streams list: ${streams}`
    )

    return streams.length ? streams[0] : this.log.error('No stream found.')
  }

  getFallbackStream (streams) {
    return streams.find((stream) => stream.locale === fallbackStreamLocale)
  }

  getUserConfigKey (watchType) {
    return userConfigKeySuffix + '-' + watchType
  }

  getUserConfig (watchType) {
    const userConfigKey = this.getUserConfigKey(watchType)

    try {
      return JSON.parse(window.localStorage.getItem(userConfigKey))
    }
    catch (ex) {
      this.log.error('Bad user config data. Resetting.')
      window.localStorage.removeItem(userConfigKey)

      return {}
    }
  }

  getStatsLayoutFromSettings () {
    const settings = store.get('settings')

    return util.getDeepValue(settings, 'watchSettings.statsLayout')
  }

  updateStatsLayoutPreference (layout) {
    const settings = store.get('settings')

    if (settings) {
      settings.watchSettings.statsLayout = layout
      return settingsService.updatePreferences(settings)
    }

    store.onChange(
      'settings',
      this.settingsListener = (settings) => {
        settings.watchSettings.statsLayout = layout
        settingsService.updatePreferences(settings)

        store.removeListener('settings', this.settingsListener)
      }
    )
  }

  setUserConfig (key, value, watchType) {
    const userConfigKey = this.getUserConfigKey(watchType)
    const config = this.getUserConfig() || {}

    config[key] = value
    window.localStorage.setItem(userConfigKey, JSON.stringify(config))
  }

  forceVideoPlayerReattach () {
    $(window).trigger('resize')
  }

  isInGame (timestamp) {
    const initialTimestamp = store.get('watch.initialTimestamp')

    return (
      initialTimestamp !== undefined
      && timestamp !== 0
      && timestamp >= util.toMilliseconds(initialTimestamp)
    )
  }

  getStatsDisabledConditions (state) {
    const gameIsLive = state.currentGame !== undefined

    const offsetIsNotZero
      = state.selectedStream !== undefined
      && state.selectedStream.offset !== undefined
      && state.selectedStream.offset !== 0

    const isInGame = state.isInGame

    const hasFirstFrame
      = state.selectedStream && state.selectedStream.firstFrameTime

    return { gameIsLive, offsetIsNotZero, isInGame, hasFirstFrame }
  }

  conditionsHaveChanged (prevConditions, nextConditions) {
    const keys = Object.keys(nextConditions)

    for (let i = 0; i < keys.length; i++) {
      if (prevConditions[keys[i]] !== nextConditions[keys[i]]) return true
    }

    return false
  }

  statsDisabledLogging (prevState, nextState) {
    const previousConditions = this.getStatsDisabledConditions(prevState)
    const nextConditions = this.getStatsDisabledConditions(nextState)

    if (!this.conditionsHaveChanged(previousConditions, nextConditions)) return

    let loggingString = ''

    !nextConditions.gameIsLive && (loggingString += '\n - No game set to live')

    !nextConditions.hasFirstFrame
      && !nextConditions.offsetIsNotZero
      && (loggingString += '\n - Offset is set to 0')

    nextState.currentGame
      && nextState.currentGame.state === 'completed'
      && !nextConditions.offsetIsNotZero
      && !nextConditions.hasFirstFrame
      && (loggingString += '\n - First frame time is not set')

    !nextConditions.isInGame && (loggingString += '\n - Not in game')

    return loggingString !== ''
      ? this.log.info('Conditions changed, stats disabled: ' + loggingString)
      : this.log.info('Conditions changed, stats enabled! All conditions met')
  }

  get streamTypes () {
    return {
      live: 'live',
      vod: 'vod'
    }
  }

  removeProvidersWithoutIESupport (streams) {
    return streams.filter(
      (stream) => providersWithoutIESupport.indexOf(stream.provider) === -1
    )
  }
}

export default new WatchUtil()
